import { motion } from "framer-motion";
import { Products } from "@/components/nyla/Products";
import { HealthBenefits } from "@/components/nyla/HealthBenefits";
import { AlkalinePosition } from "@/components/nyla/AlkalinePosition";
import { CtaBanner } from "@/components/nyla/CtaBanner";
import { SEO } from "@/components/nyla/SEO";

const rows = [
  { label: "pH", alkaline: "8–8.5 — precisely maintained", balanced: "7.5 — gently balanced", sparkling: "Balanced · lightly effervescent" },
  { label: "Character", alkaline: "For those who prefer a higher pH", balanced: "For the everyday elevated", sparkling: "For moments worth marking" },
  { label: "Minerals", alkaline: "Elevated Magnesium & Calcium", balanced: "Balanced Magnesium & Calcium", sparkling: "Delicately mineralised" },
  { label: "Best for", alkaline: "A smooth, refined everyday drinking experience", balanced: "Daily hydration, every meal, every moment", sparkling: "Dining, celebrations, palate refreshment" },
  { label: "Sizes", alkaline: "330 ml · 660 ml", balanced: "330 ml · 660 ml", sparkling: "330 ml · 660 ml" },
];

export default function ProductsPage() {
  const productsJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://nylaairwater.earth/" },
        { "@type": "ListItem", position: 2, name: "Products", item: "https://nylaairwater.earth/products" },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: [
        {
          "@type": "Product",
          position: 1,
          name: "Nyla Alkaline Air Water",
          description: "Atmospheric drinking water with an alkaline pH of 8–8.5 achieved through precise mineral enrichment, with an elevated Magnesium and Calcium profile.",
          brand: { "@type": "Brand", name: "Nyla Air Water" },
          category: "Bottled Alkaline Water",
        },
        {
          "@type": "Product",
          position: 2,
          name: "Nyla pH Balanced Air Water",
          description: "pH 7.5 gently balanced atmospheric water crafted for everyday hydration.",
          brand: { "@type": "Brand", name: "Nyla Air Water" },
          category: "Bottled Mineral Water",
        },
        {
          "@type": "Product",
          position: 3,
          name: "Nyla Sparkling Air Water",
          description: "Delicately carbonated atmospheric water — crisp, invigorating, celebration-ready.",
          brand: { "@type": "Brand", name: "Nyla Air Water" },
          category: "Sparkling Water",
        },
      ],
    },
  ];
  return (
    <div data-testid="products-page" className="pt-14">
      <SEO
        title="Products — Alkaline, pH Balanced & Sparkling"
        description="Three signature waters from Nyla — Alkaline pH 8–8.5, pH Balanced 7.5, and delicately-carbonated Sparkling. Same atmospheric purity, three distinct rituals — communicated responsibly."
        path="/products"
        keywords="Nyla alkaline water, Nyla sparkling water, pH balanced water, 330ml bottled water, 660ml bottled water, luxury water India, alkaline pH water"
        jsonLd={productsJsonLd}
      />
      <Products />
      <HealthBenefits />
      <AlkalinePosition />

      <section data-testid="compare-section" className="py-24 md:py-32 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="text-center mb-14 max-w-3xl mx-auto"
          >
            <p className="text-xs md:text-sm tracking-[0.2em] uppercase font-semibold text-[#86868B] mb-6">Side by Side</p>
            <h2 data-testid="compare-heading" className="font-display text-4xl md:text-5xl leading-tight tracking-tight font-bold text-[#1D1D1F]">
              Which Nyla is for you?
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-[#86868B]">
              Every bottle begins as the same pure atmospheric water. The craft — and the mood — is
              what sets each one apart.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="bg-[#F5F5F7] rounded-3xl overflow-hidden"
          >
            <div className="grid grid-cols-4 border-b border-[#D2D2D7]/60">
              <div className="p-5 md:p-7" />
              <div className="p-5 md:p-7 font-display text-sm md:text-lg font-bold text-[#1D1D1F]">Alkaline</div>
              <div className="p-5 md:p-7 font-display text-sm md:text-lg font-bold text-[#1D1D1F]">pH Balanced</div>
              <div className="p-5 md:p-7 font-display text-sm md:text-lg font-bold text-[#1D1D1F]">Sparkling</div>
            </div>
            {rows.map((r, i) => (
              <div key={r.label} className={`grid grid-cols-4 ${i < rows.length - 1 ? "border-b border-[#D2D2D7]/60" : ""}`}>
                <div className="p-5 md:p-7 text-[11px] md:text-xs font-semibold uppercase tracking-[0.15em] text-[#86868B]">{r.label}</div>
                <div className="p-5 md:p-7 text-xs md:text-sm text-[#1D1D1F]/80 leading-relaxed">{r.alkaline}</div>
                <div className="p-5 md:p-7 text-xs md:text-sm text-[#1D1D1F]/80 leading-relaxed">{r.balanced}</div>
                <div className="p-5 md:p-7 text-xs md:text-sm text-[#1D1D1F]/80 leading-relaxed">{r.sparkling}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <CtaBanner />
    </div>
  );
}
